<script setup>
const props = defineProps(["gallery"]);

const gridSize = 3;
const lastIndex = gridSize - 1;
const blurLast = ref(props.gallery.length > gridSize);
const currentSlide = ref(null);

const openFullscreen = (index) => {
  currentSlide.value = index;
  document.body.classList.add("overlay-open"); // Add class to disable scrolling
};

const closeFullscreen = () => {
  currentSlide.value = null;
  document.body.classList.remove("overlay-open"); // Remove class to enable scrolling
};

const handleKeydown = (event) => {
  if (currentSlide.value !== null) {
    switch (event.key) {
      case "ArrowLeft":
        prevSlide();
        break;
      case "ArrowRight":
        nextSlide();
        break;
      case "Escape":
        closeFullscreen();
        break;
    }
  }
};

const handleScroll = (event) => {
  if (currentSlide.value !== null) {
    closeFullscreen();
  }
};

onMounted(() => {
  if (currentSlide.value !== null) {
    document.body.classList.add("overlay-open");
  }
  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("wheel", handleScroll);
});

onUnmounted(() => {
  document.body.classList.remove("overlay-open");
  window.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("wheel", handleScroll);
});

const displayedImages = ref(
  blurLast.value ? props.gallery.slice(0, gridSize) : props.gallery
);

const loadAll = () => {
  displayedImages.value = props.gallery;
  blurLast.value = false;
};

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % props.gallery.length;
};

const prevSlide = () => {
  currentSlide.value =
    (currentSlide.value - 1 + props.gallery.length) % props.gallery.length;
};
</script>

<template>
  <div
    v-if="gallery"
    class="grid grid-cols-1 gap-3 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 md:gap-4 md:mb-24"
  >
    <div
      v-for="(image, index) in displayedImages"
      :key="image.id"
      class="w-full max-w-[480px] relative"
    >
      <img
        :src="image.mediaItemUrl"
        :class="{ blurred: blurLast && index === lastIndex }"
        class="w-full aspect-square object-cover cursor-pointer"
        @click.stop="openFullscreen(index)"
      />
      <div
        v-if="blurLast && index === lastIndex"
        class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center cursor-pointer"
      >
        <div
          @click.stop="loadAll()"
          class="bg-black text-white rounded-full w-12 h-12 flex items-center justify-center cursor-pointer"
        >
          +{{ gallery.length - lastIndex }}
        </div>
      </div>
    </div>
    <transition name="overlay-fade">
      <div
        class="overlay"
        v-if="currentSlide !== null"
        @click.stop="closeFullscreen"
      >
        <div class="fixed left-0 top-1/2 transform -translate-y-1/2">
          <button
            @click.stop="prevSlide()"
            class="p-2 rounded-r-md cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>
        <div class="slide--image">
          <img :src="gallery[currentSlide].mediaItemUrl" />
        </div>
        <div class="fixed right-0 top-1/2 transform -translate-y-1/2">
          <button
            @click.stop="nextSlide()"
            class="p-2 rounded-l-md cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        <div class="absolute bottom-0 left-0 right-0 flex justify-center pb-1">
          <b>{{ currentSlide + 1 }} / {{ gallery.length }}</b>
        </div>
      </div>
    </transition>
  </div>
</template>

<style>
.overlay {
  height: 100vh;
  width: 100%;
  background-color: rgb(255, 200, 0, 0.9);
  z-index: 10000;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay--image {
  max-height: 100%;
  max-width: 100%;
  object-position: contain;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.35s;
  /* transition: top 0.35s; */
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
  /* top: -100%; */
}

.overlay-open {
  overflow: hidden;
}
button {
  transition-duration: 350ms;
}
</style>
